import React from "react";
import { useAuthCtx } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { ShieldAlert, LogOut } from "lucide-react";

// Keep visually consistent with the redesigned landing/sign-in experience.
const HERO_IMAGE = null; // mirror SignIn.js — set to client photo path when available
const HERO_PLACEHOLDER_GRADIENT =
  "linear-gradient(180deg, hsl(205 42% 68%) 0%, hsl(200 40% 78%) 22%, hsl(40 55% 88%) 50%, hsl(35 68% 80%) 72%, hsl(28 62% 70%) 100%)";

export default function NotAuthorized() {
  const { email, signOut } = useAuthCtx();

  const heroStyle = HERO_IMAGE
    ? { backgroundImage: `url("${HERO_IMAGE}")` }
    : { backgroundImage: HERO_PLACEHOLDER_GRADIENT };

  return (
    <div className="relative min-h-[100svh] w-full overflow-hidden bg-[hsl(var(--pc-ink))]">
      {/* destination background + overlays (matches landing) */}
      <div className="absolute inset-0 bg-cover bg-center" style={heroStyle} aria-hidden="true" />
      <div
        className="absolute inset-0 bg-[hsl(var(--pc-ink)/0.55)] backdrop-blur-md"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay cream-grain"
        aria-hidden="true"
      />

      <div className="relative z-10 grid min-h-[100svh] place-items-center px-5">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="att-card att-glass w-full max-w-[30rem] rounded-[22px] p-7 text-center text-cream shadow-[0_24px_80px_hsl(var(--pc-ink)/0.65)] sm:p-9"
        >
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl border border-[hsl(var(--pc-line))] bg-[hsl(var(--pc-surface2)/0.6)]">
            <ShieldAlert className="h-8 w-8 text-[hsl(var(--pc-gold))]" />
          </div>

          <div className="mt-6 text-[11px] font-semibold uppercase tracking-[0.28em] text-[hsl(var(--pc-gold))]">
            President&apos;s Club 2026
          </div>
          <h1
            className="mt-2 font-serif text-3xl leading-tight"
            data-testid="not-authorized-title"
          >
            We couldn&apos;t find your invitation
          </h1>
          <p
            className="mx-auto mt-3 max-w-sm text-[15px] leading-relaxed text-cream-dim"
            data-testid="not-authorized-message"
          >
            We couldn&apos;t find
            {email ? <span className="font-semibold text-cream"> {email} </span> : " this email "}
            on the President&apos;s Club 2026 attendee list. Please sign in with the email
            address you registered with, or contact the Events team for assistance.
          </p>

          <button
            data-testid="not-authorized-signout-button"
            onClick={signOut}
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-full border border-[hsl(var(--pc-line))] bg-[hsl(var(--pc-surface2)/0.5)] px-6 py-3 font-semibold text-cream transition-colors duration-200 hover:bg-[hsl(var(--pc-surface2)/0.85)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--pc-gold)/0.5)]"
          >
            <LogOut className="h-4 w-4" /> Sign out &amp; try another email
          </button>
        </motion.div>
      </div>
    </div>
  );
}
